import { AnimationClip } from 'three'
import { type SkeletonType } from '../../lib/enums/SkeletonType'
import {
  type AnimationClipMetadata,
  type TransformedAnimationClipPair
} from '../anim-library/interfaces/TransformedAnimationClipPair'
import { get_db, USER_CLIPS_STORE } from './db'

/**
 * IndexedDB persistence for user-authored animation clips.
 *
 * What's stored: only `source_type === 'user-authored'` clips, scoped per
 * `SkeletonType` (so a user's recorded human walk doesn't show up when they
 * load a fox model). Each store entry is keyed by skeleton type and holds an
 * array of serialized clip pairs.
 *
 * What's NOT stored:
 *  - default-library clips (loaded from static GLB files on every app start)
 *  - custom-import clips (user re-imports per session)
 *  - user-uploaded base models (raw bytes are large; users re-load per session)
 *
 * Serialization uses Three.js's built-in `AnimationClip.toJSON` / `parse`,
 * which round-trips track times / values / interpolation correctly.
 *
 * Failure handling: any IDB error (private browsing, quota, missing API) is
 * caught and logged. The app continues to work in-memory; persistence simply
 * becomes a no-op.
 */

const STORE_NAME = USER_CLIPS_STORE

interface SerializedClipPair {
  display_clip: ReturnType<typeof AnimationClip.toJSON>
  original_clip: ReturnType<typeof AnimationClip.toJSON>
  metadata: AnimationClipMetadata
}

interface StoredEntry {
  skeleton_type: SkeletonType
  clips: SerializedClipPair[]
  saved_at: number
}

function serialize_pair (pair: TransformedAnimationClipPair): SerializedClipPair {
  return {
    display_clip: AnimationClip.toJSON(pair.display_animation_clip),
    original_clip: AnimationClip.toJSON(pair.original_animation_clip),
    metadata: { ...pair.metadata }
  }
}

function deserialize_pair (rec: SerializedClipPair): TransformedAnimationClipPair {
  return {
    display_animation_clip: AnimationClip.parse(rec.display_clip),
    original_animation_clip: AnimationClip.parse(rec.original_clip),
    metadata: rec.metadata
  }
}

export class UserClipStorage {
  /**
   * Persist all user-authored clips for the given skeleton type. Non-user-authored
   * clips in the input array are ignored. Replaces any previous entry for that
   * skeleton type — caller passes the full current set.
   */
  static async save (skeleton_type: SkeletonType, all_pairs: TransformedAnimationClipPair[]): Promise<void> {
    const user_pairs = all_pairs.filter(p => p.metadata.source_type === 'user-authored')
    try {
      const db = await get_db()
      const entry: StoredEntry = {
        skeleton_type,
        clips: user_pairs.map(serialize_pair),
        saved_at: Date.now()
      }
      await db.put(STORE_NAME, entry)
    } catch (err) {
      console.warn('UserClipStorage.save failed (clips will not persist this session):', err)
    }
  }

  /** Load saved user-authored clips for the given skeleton type. Returns empty array on miss or error. */
  static async load (skeleton_type: SkeletonType): Promise<TransformedAnimationClipPair[]> {
    try {
      const db = await get_db()
      const entry = await db.get(STORE_NAME, skeleton_type) as StoredEntry | undefined
      if (entry === undefined) return []
      return entry.clips.map(deserialize_pair)
    } catch (err) {
      console.warn('UserClipStorage.load failed:', err)
      return []
    }
  }

  /** Clear all user clips for one skeleton type. */
  static async clear (skeleton_type: SkeletonType): Promise<void> {
    try {
      const db = await get_db()
      await db.delete(STORE_NAME, skeleton_type)
    } catch (err) {
      console.warn('UserClipStorage.clear failed:', err)
    }
  }

  /** Clear ALL user clips across all skeleton types. Used by the "reset saved data" UI. */
  static async clear_all (): Promise<void> {
    try {
      const db = await get_db()
      await db.clear(STORE_NAME)
    } catch (err) {
      console.warn('UserClipStorage.clear_all failed:', err)
    }
  }
}
